import React from "react";
// ANT DESIGN COMPONENTS
import { Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CONFIG
import { ActionTypeConfig } from "../actionTypeConfig";

export interface ActionCommandSummary {
  /** The operator-configured label for this command. */
  label: string;
  /** Human-readable name of the bound Control message, "" when unbound. */
  commandName: string;
  /** Human-readable names of the states in which this command is unavailable. */
  validStateNames: string[];
  /** Configured timer in seconds; 0 or undefined hides the row. */
  timer?: number;
  /**
   * Catalog ids, already formatted as hyphenated UUID strings by the caller —
   * they arrive from the API as FlatBuffer UuidT values, and a raw one renders
   * as "[object Object]". "" when the row does not carry the id.
   */
  actionId?: string;
  messageId?: string;
}

/**
 * The expanded body of an action card: the type's explanation, the derived
 * metadata line, and a per-command breakdown (bound message, valid states,
 * timer).
 *
 * This was a hover popover behind an info icon. The content is unchanged — what
 * changed is that it now lives inside the card, which expands: an operator
 * comparing two rows had to hover each icon in turn and could never see both,
 * and on a touch screen the hover trigger was a coin flip. Each command block
 * is titled, which the popover never did: it stacked the primary's rows
 * directly above the paired command's with no divider, so "Valid states" read
 * as belonging to whichever command you assumed you were looking at.
 */
export default function ActionDetails({
  config,
  primary,
  secondary = undefined,
  metadata = [],
}: {
  config: ActionTypeConfig;
  primary: ActionCommandSummary;
  /** The paired command. Every action type has one, so ActionCard always
   *  passes it; the prop stays optional for callers documenting a lone one. */
  secondary?: ActionCommandSummary;
  /** Short derived fragments (cooldown, sequence, hint) shown above the
   *  per-command breakdown. */
  metadata?: string[];
}) {
  const [t] = useTranslation("global");

  const renderCommand = (title: string, summary: ActionCommandSummary) => (
    <div className="actionInfo__command">
      <span className="actionInfo__commandTitle">{title}</span>
      <div className="actionInfo__row">
        <span className="actionInfo__rowLabel">{t("general.label")}</span>
        <span className="actionInfo__rowValue">{summary.label || "--"}</span>
      </div>
      {summary.commandName !== "" && (
        <div className="actionInfo__row">
          <span className="actionInfo__rowLabel">{t("general.command")}</span>
          <span className="actionInfo__rowValue">{summary.commandName}</span>
        </div>
      )}
      {!!summary.timer && summary.timer > 0 && (
        <div className="actionInfo__row">
          <span className="actionInfo__rowLabel">{t("general.activeTime")}</span>
          <span className="actionInfo__rowValue">
            <Tag color="green">{summary.timer}s</Tag>
          </span>
        </div>
      )}
      {/* The row is unconditional. Hiding it when the list was empty made "no
          valid states" (the command is never gated) indistinguishable from a
          card that simply does not report them, and the two mean opposite
          things for whether the button can go inert. */}
      <div className="actionInfo__row">
        <span className="actionInfo__rowLabel">{t("general.validStates")}</span>
        <span className="actionInfo__rowValue">
          {summary.validStateNames.length > 0 ? (
            summary.validStateNames.map((name) => (
              <Tag color="cyan" key={name}>
                {name}
              </Tag>
            ))
          ) : (
            <Tag>{t("monitoringActions.noValidStates", "No valid states")}</Tag>
          )}
        </span>
      </div>
      <div className="actionInfo__row">
        <span className="actionInfo__rowLabel">{t("monitoringActions.actionId", "Action ID")}</span>
        <span className="actionInfo__rowValue actionInfo__rowValue--id">
          {summary.actionId || "--"}
        </span>
      </div>
      <div className="actionInfo__row">
        <span className="actionInfo__rowLabel">
          {t("monitoringActions.messageId", "Message ID")}
        </span>
        <span className="actionInfo__rowValue actionInfo__rowValue--id">
          {summary.messageId || "--"}
        </span>
      </div>
    </div>
  );

  const { guide } = config;

  return (
    <div className="actionInfo">
      <p className="actionInfo__description">{t(guide.intro.key, guide.intro.text)}</p>
      {guide.bullets && guide.bullets.length > 0 && (
        <ul className="actionInfo__bullets">
          {guide.bullets.map((bullet) => (
            <li key={bullet.key}>{t(bullet.key, bullet.text)}</li>
          ))}
        </ul>
      )}
      {guide.outro && (
        <p className="actionInfo__description">{t(guide.outro.key, guide.outro.text)}</p>
      )}
      {metadata.length > 0 && (
        <div className="actionCard__secondary">
          {metadata.map((entry, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <span className="actionCard__meta" key={`${entry}-${index}`}>
              {index > 0 && <span className="actionCard__metaDot" />}
              {entry}
            </span>
          ))}
        </div>
      )}
      <div className="actionInfo__commands">
        {renderCommand(t("monitoringActions.primaryCommand", "Primary command"), primary)}
        {secondary &&
          renderCommand(t("monitoringActions.pairedCommand", "Paired command"), secondary)}
      </div>
    </div>
  );
}
